import { api } from './client.js';

/** 원본 /xadm/sale_month_list.php (매출 월별) */
export interface MonthSales {
  month: string;
  orderCount: number;
  sales: number;
}

export interface DaySales {
  date: string;
  orderCount: number;
  sales: number;
}

/** 원본 /xadm/visit_list.php (방문) */
export interface VisitDay {
  date: string;
  weekday: number;
  pc: number;
  mobile: number;
  total: number;
}

export interface VisitHour {
  hour: number;
  pc: number;
  mobile: number;
  total: number;
}

/** 원본 /xadm/ord_list.php (주문, "추가정보" 애드워즈/GA 모드는 제외) */
export interface OrderDay {
  date: string;
  weekday: number;
  orderCount: number;
  sales: number;
}

interface Metric {
  cumulative: number;
  delta: number;
}

export interface OrderHour {
  hour: number;
  order: Metric;
  orderSlim: Metric;
  sale: Metric;
  saleSlim: Metric;
  visit: Metric;
}

/** 원본 /xadm/new_member_list.php (신규가입자) */
export interface MemberDay {
  date: string;
  weekday: number;
  pc: number;
  mobile: number;
  banana: number;
  slim: number;
  normal: number;
  social: number;
  naver: number;
  google: number;
  twitter: number;
  total: number;
}

export type MemberHour = Omit<MemberDay, 'date' | 'weekday'> & { hour: number };

/** 원본 /xadm/delivery_list.php (배송현황) */
export interface LogisticsHour {
  seq: number;
  date: string;
  weekday: number;
  hour: number;
  total: number;
  done: number;
  work: number;
  recordedAt: string | null;
  /** 오늘(KST) 행인가 - 서버가 판정한다. 이월 배지를 붙일 행이다. */
  isToday: boolean;
}

export interface LogisticsPage {
  weekday: number;
  page: number;
  pageSize: number;
  total: number;
  rows: LogisticsHour[];
  /** 오늘 날짜(KST). */
  today: string;
  /** 전날 미처리 이월 건수 - 전광판과 같은 숫자. 오늘 행이 없으면 0. */
  carriedOver: number;
}

export interface LogisticsMinute {
  minute: number;
  total: number;
  done: number;
  work: number;
}

/** 원본 /xadm/delivery_status_inframe.php (배송현황 카운터) */
export interface CarriedOverOrder {
  gotableIdx: number;
  invoiceNo: string;
  orderIds: number[];
  orderNo: string;
  orderStatus: string;
  orderStatusLabel: string;
}

export interface InvoiceCounter {
  total: number;
  processed: number;
  notProcessed: number;
  /** 전날 이월분 중 아직 미처리인 건수. 서버가 세고, 커스텀 기간 조회면 0 이다. */
  carriedOver: number;
  /** 전날 이월분 중 오늘 처리된 건수(bizhost 오늘 숫자에는 안 들어온다). */
  carriedOverDone: number;
  carriedOverOrders: CarriedOverOrder[];
  /** 이월을 더한 값 - 전광판이 표시하는 숫자다(CsReportPage 는 위 원본 값을 쓴다). */
  withCarryOver: { total: number; processed: number; notProcessed: number };
}


/** 애드워즈 현황 — 원본 /xadmin/report_adw.php (banana.shop_stats_adw) */
export interface AdwordsDay {
  date: string;
  weekday: number;
  lastHour: number;
  conv: number;
  click: number;
  imp: number;
  cost: number;
  convcost: number;
  porder: number;
  pconvcost: number;
  clickRate: number | null;
  profitRate: number | null;
  avgOrder: number | null;
  clickCost: number | null;
  pProfitRate: number | null;
  pAvgOrder: number | null;
}

export interface AdwordsHour {
  hour: number;
  convPerHour: number;
  conv: number;
  click: number;
  imp: number;
  cost: number;
  convcost: number;
  porder: number;
  pconvcost: number;
  clickRate: number | null;
  profitRate: number | null;
  avgOrder: number | null;
  pProfitRate: number | null;
  pAvgOrder: number | null;
}

export type AdwordsSort = 'dates' | 'conv' | 'click' | 'imp' | 'cost' | 'convcost' | 'porder';

export interface AdwordsDaily {
  from: string;
  to: string;
  weekday: number | null;
  sort: AdwordsSort;
  days: AdwordsDay[];
  totals: Omit<AdwordsDay, 'date' | 'weekday' | 'lastHour'>;
}


/** CS 리포트 — 원본 /xadmin/cs_report.php (banana_custom.inhouse_cs_report) */
export interface CsReportDay {
  date: string;
  weekday: number;
  lastHour: number;
  callCount: number;
  qnaCount: number;
}

export interface CsReportTotals {
  callCount: number;
  qnaCount: number;
  adminOrder: number;
  devTotal: number;
  devIng: number;
  devStay: number;
}

export interface CsReportHour extends CsReportTotals {
  hour: number;
  subtotal?: CsReportTotals & { label: string };
}

export interface CsReportList {
  from: string;
  to: string;
  weekday: number | null;
  days: CsReportDay[];
  months: { month: string; year: string }[];
  /** 아직 테이블이 없으면 true (migrations/004_inhouse_cs_report.sql 미적용) */
  tableMissing: boolean;
}

export interface CsReportDetail {
  date: string;
  weekday: number;
  hours: CsReportHour[];
  totals: CsReportTotals;
}

export const xadmApi = {
  salesYears: () => api.get<{ years: string[] }>('/xadm/sales/years'),
  salesMonthly: (year: string) =>
    api.get<{ year: string; months: MonthSales[] }>(`/xadm/sales/monthly?year=${encodeURIComponent(year)}`),
  salesDaily: (month: string) =>
    api.get<{ month: string; days: DaySales[] }>(`/xadm/sales/daily?month=${encodeURIComponent(month)}`),

  visitDays: (weekday?: number) =>
    api.get<{ weekday: number; days: VisitDay[] }>(
      `/xadm/visits/days${weekday === undefined ? '' : `?weekday=${weekday}`}`,
    ),
  visitHours: (date: string) =>
    api.get<{ date: string; hours: VisitHour[] }>(`/xadm/visits/hours?date=${encodeURIComponent(date)}`),

  orderDays: (weekday?: number) =>
    api.get<{ weekday: number; days: OrderDay[] }>(
      `/xadm/orders/days${weekday === undefined ? '' : `?weekday=${weekday}`}`,
    ),
  orderHours: (date: string) =>
    api.get<{ date: string; hours: OrderHour[] }>(`/xadm/orders/hours?date=${encodeURIComponent(date)}`),

  memberDays: (weekday?: number) =>
    api.get<{ weekday: number; days: MemberDay[] }>(
      `/xadm/members/days${weekday === undefined ? '' : `?weekday=${weekday}`}`,
    ),
  memberHours: (date: string) =>
    api.get<{ date: string; hours: MemberHour[] }>(`/xadm/members/hours?date=${encodeURIComponent(date)}`),

  logisticsHours: (weekday: number | undefined, page: number) => {
    const q = new URLSearchParams({ page: String(page) });
    if (weekday !== undefined) q.set('weekday', String(weekday));
    return api.get<LogisticsPage>(`/xadm/logistics/hours?${q.toString()}`);
  },
  logisticsMinutes: (date: string, hour: number) =>
    api.get<{ date: string; hour: number; minutes: LogisticsMinute[] }>(
      `/xadm/logistics/minutes?date=${encodeURIComponent(date)}&hour=${hour}`,
    ),

  adwords: (p: { from?: string; to?: string; weekday?: number | null; sort?: AdwordsSort }) => {
    const q = new URLSearchParams();
    if (p.from) q.set('from', p.from);
    if (p.to) q.set('to', p.to);
    if (p.weekday !== null && p.weekday !== undefined) q.set('weekday', String(p.weekday));
    if (p.sort) q.set('sort', p.sort);
    const qs = q.toString();
    return api.get<AdwordsDaily>(`/xadm/adwords${qs ? `?${qs}` : ''}`);
  },
  csReport: (p: { from?: string; to?: string; weekday?: number | null; month?: string }) => {
    const q = new URLSearchParams();
    if (p.from) q.set('from', p.from);
    if (p.to) q.set('to', p.to);
    if (p.weekday !== null && p.weekday !== undefined) q.set('weekday', String(p.weekday));
    if (p.month) q.set('month', p.month);
    const qs = q.toString();
    return api.get<CsReportList>(`/xadm/cs-report${qs ? `?${qs}` : ''}`);
  },
  csReportHours: (date: string) =>
    api.get<CsReportDetail>(`/xadm/cs-report/hours?date=${encodeURIComponent(date)}`),

  adwordsHours: (date: string) =>
    api.get<{ date: string; hours: AdwordsHour[] }>(`/xadm/adwords/hours?date=${encodeURIComponent(date)}`),

  logisticsCounter: (start?: string, end?: string) => {
    const q = new URLSearchParams();
    if (start) q.set('start', start);
    if (end) q.set('end', end);
    const qs = q.toString();
    return api.get<InvoiceCounter>(`/xadm/logistics/counter${qs ? `?${qs}` : ''}`);
  },

};
